import React, { createContext, useContext, useState } from 'react';
import { useAuthHelper } from '../../components/AuthContextProvider';
import { Data, DataState } from '../../types/data-providers';
import * as ApiRequests from '../../api/requests';
import { VENDOR_NAME, BLUEPRINT_ORGANIZATION_NAME } from '../../constants';
import { ApiResponse } from '../../api/api-request-helpers';
import { DeviceMasterDataOkResponse, GenericNotOkResponse } from '../../api/types';
import { timeRange } from '../../constants/static-data';

type MasterData = {
    deviceTypeData: Data<any>;
    devicesData: Data<any>;
    deviceInfo: Data<any>;
    trendTimeInfo: Data<any>;
    getDeviceTypeMasterData: Function;
    getDevicesMasterData: Function;
    getDevicesInfo: Function;
    getTrendTimeInfo: Function;
    rowKey: string;
    pxdbRowKey: string;
};

const Context = createContext<MasterData | null>(null);

function useDeviceMaster(): MasterData {
    const contextState = useContext(Context);
    if (contextState === null) {
        throw new Error('useDeviceMaster must be used within a DeviceMasterProvider');
    }
    return contextState;
}

const DeviceMasterProvider: React.FC = (props) => {
    const authHelper = useAuthHelper();
    const [deviceTypeData, setDeviceTypeData] = useState<Data<any>>({ state: DataState.Loading });
    const [devicesData, setDevicesData] = useState<Data<any>>({ state: DataState.Loading });
    const [deviceInfo, setDeviceInfo] = useState<Data<any>>({ state: DataState.Loading });
    const [trendTimeInfo, setTrendTimeInfo] = useState<Data<any>>({ state: DataState.Loading });
    const [rowKey, setRowKey] = useState('');
    const [pxdbRowKey, setPxdbRowKey] = useState('');

    const getDataByAPI = async (
        orgName: string,
        key: string,
        customerName?: string,
        type?: string
    ): Promise<ApiResponse<DeviceMasterDataOkResponse, GenericNotOkResponse>> => {
        const deviceMasterResult = await ApiRequests.getDeviceMasterData(
            {
                vendorName: BLUEPRINT_ORGANIZATION_NAME,
                customerName: customerName !== undefined ? customerName : VENDOR_NAME,
                key: key,
                type: type,
            },
            { authHelper }
        );
        return deviceMasterResult;
    };

    const getDeviceTypeMasterData = async (customerName: string, key: string, type?: string): Promise<void> => {
        setRowKey(key);
        setDeviceTypeData({
            state: DataState.Loading,
        });
        const deviceMasterResult = await getDataByAPI(customerName, key, type);

        const result = deviceMasterResult.ok
            ? setDeviceTypeData({
                  state: DataState.Loaded,
                  value: deviceMasterResult.response.data.value,
              })
            : setDeviceTypeData({
                  state: DataState.Error,
                  errorMessage: ApiRequests.extractErrorCodeWithMessage(deviceMasterResult.response),
              });
        return result;
    };

    const getDevicesMasterData = async (customerName: string, key: string, type?: string): Promise<void> => {
        setPxdbRowKey(key);
        setDevicesData({
            state: DataState.Loading,
        });
        const deviceMasterResult = await getDataByAPI(customerName, key, type);

        const result = deviceMasterResult.ok
            ? setDevicesData({
                  state: DataState.Loaded,
                  value: deviceMasterResult.response.data.value,
              })
            : setDevicesData({
                  state: DataState.Error,
                  errorMessage: ApiRequests.extractErrorCodeWithMessage(deviceMasterResult.response),
              });
        return result;
    };
    const getDevicesInfo = async (customerName: string, key: string, type?: string): Promise<void> => {
        setRowKey(key);
        setDeviceInfo({
            state: DataState.Loading,
        });
        const deviceMasterResult = await getDataByAPI(customerName, key, type);

        const result = deviceMasterResult.ok
            ? setDeviceInfo({
                  state: DataState.Loaded,
                  value: deviceMasterResult.response.data.value,
              })
            : setDeviceInfo({
                  state: DataState.Error,
                  errorMessage: ApiRequests.extractErrorCodeWithMessage(deviceMasterResult.response),
              });

        return result;
    };

    const getTrendTimeInfo = async (orgName: string, customerName: string, key: string): Promise<void> => {
        setRowKey(key);
        setTrendTimeInfo({
            state: DataState.Loading,
        });
        const TrendTimeResult = await getDataByAPI(orgName, key, customerName);

        const result = TrendTimeResult.ok
            ? setTrendTimeInfo({
                  state: DataState.Loaded,
                  value:
                      TrendTimeResult.response.data.value === undefined
                          ? timeRange
                          : TrendTimeResult.response.data.value,
              })
            : setTrendTimeInfo({
                  state: DataState.Error,
                  errorMessage: ApiRequests.extractErrorCodeWithMessage(TrendTimeResult.response),
              });
        return result;
    };

    return (
        <Context.Provider
            value={{
                deviceTypeData,
                devicesData,
                deviceInfo,
                trendTimeInfo,
                getDeviceTypeMasterData,
                getDevicesMasterData,
                getDevicesInfo,
                getTrendTimeInfo,
                rowKey,
                pxdbRowKey,
            }}
        >
            {props.children}
        </Context.Provider>
    );
};

export { DeviceMasterProvider, useDeviceMaster };
